import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Flame, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { categoriesQuery } from "@/lib/queries";
import { promoteServiceToBoard as promoteServiceToBoardFn } from "@/lib/marketplace/board.functions";
import { useT } from "@/i18n";


type ServiceLite = {
  id: string;
  title: string;
  slug: string;
  short_description?: string | null;
  cover_url?: string | null;
  listing_id?: string | null;
};

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 70);
}

/**
 * Publishes a marketplace service onto a public board as a listing, then sends
 * the seller into the standard boost checkout to pay the entry position.
 */
export function ServiceBoardDialog({
  service,
  sellerUserId,
  sellerHandle,
  open,
  onOpenChange,
}: {
  service: ServiceLite | null;
  sellerUserId: string;
  sellerHandle: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useT();
  const navigate = useNavigate();
  const promoteServiceToBoard = useServerFn(promoteServiceToBoardFn);
  const { data: categories } = useQuery(categoriesQuery);
  const [categoryId, setCategoryId] = useState("");
  const [busy, setBusy] = useState(false);

  const options = useMemo(() => (categories ?? []).filter((c) => !c.parent_id), [categories]);
  const category = options.find((c) => c.id === categoryId);

  async function promote() {
    if (!service || !category) return;
    setBusy(true);
    try {
      const res = await promoteServiceToBoard({
        data: { serviceId: service.id, categoryId: category.id },
      });
      onOpenChange(false);
      void navigate({ to: "/boost", search: { listing: res.listingSlug, category: res.categorySlug } });
    } catch (e) {
      const msg =
        typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : String(e);
      toast.error(msg || t("mpsell.board.errorDefault"));
    } finally {
      setBusy(false);
    }
  }


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("mpsell.board.title")}</DialogTitle>
          <DialogDescription>
            {t("mpsell.board.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>{t("mpsell.board.board")}</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder={t("mpsell.board.chooseBoard")} />
              </SelectTrigger>
              <SelectContent>
                {options.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="hot" className="w-full" disabled={!categoryId || busy} onClick={() => void promote()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Flame className="size-4" />}
            {t("mpsell.board.continueToPayment")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
